import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const AuthModal = ({ onClose }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const { login, register } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (isLogin) {
                await login(formData.email, formData.password);
                toast.success('Successfully logged in!');
            } else {
                await register(formData.name, formData.email, formData.password);
                toast.success('Account created successfully!');
            }
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="auth-modal">
            <div className="bg-black border border-white/10 w-full max-w-md p-8 relative">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
                    data-testid="close-modal-button"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-3xl font-bold mb-6 tracking-tight">{isLogin ? 'Login' : 'Sign Up'}</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                        <div>
                            <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                                Name
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 font-mono text-sm placeholder:text-white/30"
                                placeholder="John Doe"
                                required
                                data-testid="name-input"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 font-mono text-sm placeholder:text-white/30"
                            placeholder="you@example.com"
                            required
                            data-testid="email-input"
                        />
                    </div>

                    <div>
                        <label className="block text-sm uppercase tracking-widest text-muted-foreground mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full bg-black border-white/20 border focus:border-primary rounded-none h-12 px-4 font-mono text-sm placeholder:text-white/30"
                            placeholder="••••••••"
                            required
                            data-testid="password-input"
                        />
                    </div>

                    <button 
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full mt-6"
                        data-testid="submit-button"
                    >
                        {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button 
                        onClick={() => setIsLogin(!isLogin)}
                        className="text-primary hover:underline"
                        data-testid="toggle-auth-mode"
                    >
                        {isLogin ? 'Sign Up' : 'Login'}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default AuthModal;
